import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'
import classNames from 'classnames'
import type { DialogProps } from '../components/Dialog'
import Dialog, { DialogTitle, DialogActions, DialogContent } from '../components/Dialog'
import Button from '../components/Button'
import IconButton from '../components/IconButton'
import Typography from '../components/Typography'
import Alert from '../components/Alert'

export interface ConfirmationDialogProps extends DialogProps {
  title?: React.ReactNode
  message?: React.ReactNode
  yesLabel?: React.ReactNode
  noLabel?: React.ReactNode
  scary?: boolean
  warning?: boolean
  severity?: 'error' | 'warning' | 'info' | 'success'
  onConfirm?: (event: React.MouseEvent<HTMLElement>) => void
  onCancel?: (event: React.MouseEvent<HTMLElement>) => void
  onNoLabelClick?: (event: React.MouseEvent<HTMLElement>) => void
  closeButton?: boolean
  hideBackButton?: boolean
  alignLeftButtonWithTitle?: boolean
}

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  alert: {
    width: '100%'
  },
  scaryButton: {
    background: theme.palette.error.main
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}))
function ConfirmationDialog({
  onCancel,
  onConfirm,
  title,
  message,
  yesLabel,
  noLabel,
  scary,
  warning,
  severity = 'error',
  closeButton,
  onNoLabelClick,
  hideBackButton,
  alignLeftButtonWithTitle,
  onClose,
  ...otherProps
}: ConfirmationDialogProps) {
  const [loading, setLoading] = useState(false)
  const classes = useStyles(otherProps)
  const showAlert = !!scary || !!warning
  async function handleConfirm(ev: any) {
    // set loading is the onConfirm is an async function
    const loadingTimeout = setTimeout(() => setLoading(true), 1)
    await onConfirm(ev)

    setLoading(false)
    // cancel the timeout if the async finished quickly / wasn't async
    clearTimeout(loadingTimeout)
  }

  const closeHandler =
    !onCancel && !onClose
      ? undefined
      : (event?: React.MouseEvent<HTMLElement>, reason?: 'backdropClick' | 'escapeKeyDown') => {
          if (onCancel) {
            onCancel(event)
          }
          if (onClose) {
            onClose(event, reason)
          }
        }

  const rightContent =
    closeButton && closeHandler ? (
      <IconButton size="small" onClick={closeHandler} disabled={loading}>
        <ClearIcon />
      </IconButton>
    ) : undefined

  const cancelButtonHandler = onNoLabelClick || closeHandler

  return (
    <Dialog onClose={closeHandler} {...otherProps}>
      {title && (
        <DialogTitle
          loading={loading}
          classes={closeButton ? { root: classes.dialogTitle } : undefined}
          rightContent={rightContent}
        >
          {title}
        </DialogTitle>
      )}
      <DialogContent className={classes.content}>
        {showAlert ? (
          <Alert className={classes.alert} content={message} severity={severity} variant="default" centerIcon={false} />
        ) : (
          <Typography variant="body1" component="div" align="left">
            {message}
          </Typography>
        )}
      </DialogContent>
      {(!!onConfirm || !!cancelButtonHandler) && (
        <DialogActions
          alignLeftButtonWithTitle={alignLeftButtonWithTitle}
          leftButton={
            !hideBackButton && cancelButtonHandler ? (
              <Button disabled={loading} onClick={cancelButtonHandler} variant={'text'}>
                {noLabel}
              </Button>
            ) : null
          }
        >
          <Button
            disabled={loading}
            onClick={handleConfirm}
            variant={'contained'}
            className={classNames({
              [classes.scaryButton]: !!scary,
              'tour-confirmation-dialog-button': true
            })}
          >
            {yesLabel}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

ConfirmationDialog.defaultProps = {
  title: 'Are you sure?',
  yesLabel: "Yes, I'm Sure",
  noLabel: 'Back',
  closeButton: true
}
export default ConfirmationDialog
